export type RdwDraft = {
  plate: string;
  vin?: string | null;
  make?: string | null;
  model?: string | null;
  version?: string | null;
  bodyType?: string | null;
  colorPrimary?: string | null;
  colorSecondary?: string | null;
  doors?: number | null;
  seats?: number | null;
  fuelType?: string | null;
  powerKw?: number | null;
  engineCapacityCc?: number | null;
  cylinders?: number | null;
  co2Gkm?: number | null;
  emissionClass?: string | null;
  firstRegDate?: string | null;
  firstRegDateNL?: string | null;
  apkExpiryDate?: string | null;
  brutoBpm?: number | null;
  vehicleClass?: string | null;
  massRijklaar?: number | null;
  lengthMm?: number | null;
  widthMm?: number | null;
};

export type RdwOk = {
  found: true;
  plate: string;
  canonicalPlate: string;
  draft?: RdwDraft;  // Legacy format
  merged?: RdwDraft; // New format
  fetchedAt: string;
  source: "RDW";
  fromCache: boolean;
  forceApplied?: boolean;
  warnings: string[];
};

export type RdwErr = {
  found: false;
  reason: "INVALID_PLATE" | "NOT_FOUND" | "UPSTREAM_ERROR" | "RATE_LIMITED" | "UNAUTHORIZED" | "BAD_REQUEST";
  message: string;
  details?: {
    plate?: string;
    canonicalPlate?: string;
    retryAfter?: number;
  };
};

export type RdwResp = RdwOk | RdwErr;

export const normalizePlate = (s: string) =>
  (s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/**
 * Lookup vehicle data from RDW API
 */
export async function rdwLookup(
  plateRaw: string,
  opts?: { signal?: AbortSignal; force?: boolean }
): Promise<RdwResp> {
  const url = import.meta.env.VITE_RDW_LOOKUP_URL as string | undefined;
  if (!url) {
    throw new Error("Missing VITE_RDW_LOOKUP_URL environment variable");
  }

  const plate = normalizePlate(plateRaw);

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        // Add API key if available
        ...(import.meta.env.VITE_MODCAR_API_KEY && {
          "x-modcar-key": import.meta.env.VITE_MODCAR_API_KEY,
        }),
      },
      signal: opts?.signal,
      body: JSON.stringify({ plate, force: !!opts?.force }),
    });

    const json = await res.json().catch(() => ({}));

    if (!res.ok) {
      // Handle error responses
      const error = json as RdwErr;
      return {
        found: false,
        reason: error.reason || "UPSTREAM_ERROR",
        message: error.message || `HTTP ${res.status}: ${res.statusText}`,
        details: error.details,
      };
    }

    // Success response
    const success = json as RdwOk;
    // Support both 'merged' (new) and 'draft' (legacy) formats
    const data = success.merged || success.draft;
    return {
      found: true,
      plate: success.plate,
      canonicalPlate: success.canonicalPlate,
      draft: data,  // Always expose as 'draft' for backward compatibility
      merged: data, // Also expose as 'merged' for new code
      fetchedAt: success.fetchedAt,
      source: success.source,
      fromCache: success.fromCache,
      forceApplied: success.forceApplied,
      warnings: success.warnings || [],
    };
  } catch (error) {
    // Network error or abort
    if (error instanceof Error && error.name === "AbortError") {
      return {
        found: false,
        reason: "UPSTREAM_ERROR",
        message: "Verzoek geannuleerd",
      };
    }

    return {
      found: false,
      reason: "UPSTREAM_ERROR",
      message: error instanceof Error ? error.message : "Netwerkfout",
    };
  }
}
